/**
 * Bollinger Band Squeeze
 *
 * Detects when Bollinger Bands contract inside Keltner Channels,
 * indicating a period of low volatility that often precedes
 * explosive moves. Shows momentum histogram with squeeze dots.
 *
 * Category: Volatility | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { computeSMA, computeEMA } from "./sma";
import { computeATR } from "./atr";

export const bbSqueezeIndicator: IndicatorDefinition = {
  id: "bb_squeeze",
  name: "BB Squeeze",
  shortName: "BBSqz",
  category: "volatility",
  pane: "separate",
  params: [
    { key: "bbPeriod", label: "BB Period", type: "number", default: 20, min: 5, max: 100 },
    { key: "bbMult", label: "BB Mult", type: "number", default: 2.0, min: 0.5, max: 5, step: 0.1 },
    { key: "kcPeriod", label: "KC Period", type: "number", default: 20, min: 5, max: 100 },
    { key: "kcMult", label: "KC Mult", type: "number", default: 1.5, min: 0.5, max: 5, step: 0.1 },
  ],
  plots: [
    { key: "momentum", label: "Momentum", color: "#26A69A", style: "histogram", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { bbPeriod, bbMult, kcPeriod, kcMult } = params;
    const closes = candles.map(c => c.close);
    const bbSma = computeSMA(closes, bbPeriod);
    const kcEma = computeEMA(closes, kcPeriod);
    const atr = computeATR(candles, kcPeriod);

    const momData: { time: number; value: number }[] = [];

    for (let i = Math.max(bbPeriod, kcPeriod); i < candles.length; i++) {
      if (bbSma[i] === undefined || kcEma[i] === undefined || atr[i] === undefined) continue;

      // BB
      let sumSq = 0;
      for (let j = i - bbPeriod + 1; j <= i; j++) {
        const d = closes[j] - bbSma[i];
        sumSq += d * d;
      }
      const stdDev = Math.sqrt(sumSq / bbPeriod);
      const bbUpper = bbSma[i] + bbMult * stdDev;
      const bbLower = bbSma[i] - bbMult * stdDev;

      // KC
      const kcUpper = kcEma[i] + kcMult * atr[i];
      const kcLower = kcEma[i] - kcMult * atr[i];

      // Squeeze = BB inside KC
      const _isSqueeze = bbLower > kcLower && bbUpper < kcUpper;

      // Momentum via linear regression on (close − midline(BB+KC))
      // Simplified: use delta close from average of BB & KC midlines
      const midline = (bbSma[i] + kcEma[i]) / 2;
      const momentum = closes[i] - midline;

      momData.push({ time: candles[i].time, value: momentum });
    }

    return [{ key: "momentum", data: momData }];
  },
};
